import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Phone, MessageCircle, Mail, CalendarPlus, Cake, Clock3, MapPin, Home, ChevronRight } from "lucide-react";
import { Screen, TopBar, BackButton, ScrollArea, BottomBar, PrimaryButton } from "@/components/jk/ui";
import { Avatar, Card, Pill, SectionLabel, FullScreenLoader } from "@/components/jk/kit";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatTime } from "@/lib/format";
import api from "@/lib/api";

const DetailRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#f2f0ec] text-[#666666]">{icon}</span>
    <div>
      <p className="text-[11px] uppercase tracking-wide text-[#aaaaaa]">{label}</p>
      <p className="text-[14px] font-medium text-[#111111]">{value || "—"}</p>
    </div>
  </div>
);

const ClientProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { astrologer } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/clients/${id}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <FullScreenLoader />;
  if (!data) return null;
  const c = data.client;
  const phone = `${c.mobile_country_code || "+91"}${(c.mobile_number || "").replace(/\D/g, "")}`;

  const ContactBtn = ({ icon, label, href, testid }) => (
    <a href={href} data-testid={testid} className="flex flex-1 flex-col items-center gap-1.5 rounded-[12px] bg-[#f2f0ec] py-3 active:scale-[0.97]">
      <span className="text-[#111111]">{icon}</span>
      <span className="text-[11px] font-medium text-[#666666]">{label}</span>
    </a>
  );

  return (
    <Screen testid="client-profile-screen">
      <TopBar left={<BackButton onClick={() => navigate(-1)} />} center={<span className="text-[14px] font-semibold text-[#111111]">Client</span>} />

      <ScrollArea className="px-6">
        <div className="flex flex-col items-center pt-2 text-center">
          <Avatar name={c.full_name} photo={c.photo_url} size={72} />
          <h1 className="mt-3 text-[22px] font-bold tracking-[-0.01em] text-[#111111]" data-testid="client-name">{c.full_name}</h1>
          <p className="mt-1 text-[13px] text-[#666666]">{c.total_consultations || 0} consultation{(c.total_consultations || 0) === 1 ? "" : "s"}</p>
        </div>

        {/* Contact */}
        <div className="mt-5 flex gap-3" data-testid="contact-actions">
          <ContactBtn testid="contact-call" icon={<Phone size={18} strokeWidth={1.75} />} label="Call" href={`tel:${phone}`} />
          <ContactBtn testid="contact-whatsapp" icon={<MessageCircle size={18} strokeWidth={1.75} />} label="WhatsApp" href={`https://wa.me/${phone.replace("+", "")}`} />
          <ContactBtn testid="contact-email" icon={<Mail size={18} strokeWidth={1.75} />} label="Email" href={`mailto:${c.email}`} />
        </div>

        {/* Birth details */}
        <Card className="mt-5">
          <SectionLabel className="mb-4">Birth details</SectionLabel>
          <div className="space-y-4">
            <DetailRow icon={<Cake size={16} strokeWidth={1.75} />} label="Date of birth" value={formatDate(c.date_of_birth)} />
            <DetailRow icon={<Clock3 size={16} strokeWidth={1.75} />} label="Time of birth" value={c.time_of_birth} />
            <DetailRow icon={<MapPin size={16} strokeWidth={1.75} />} label="Place of birth" value={c.place_of_birth} />
            <DetailRow icon={<Home size={16} strokeWidth={1.75} />} label="Place of residence" value={c.place_of_residence} />
          </div>
        </Card>

        {/* Upcoming */}
        {data.upcoming && (
          <Card dark className="mt-4" data-testid="upcoming-booking">
            <SectionLabel className="text-white/60">Upcoming</SectionLabel>
            <p className="mt-2 text-[16px] font-semibold">{data.upcoming.service_name}</p>
            <p className="mt-1 text-[13px] text-white/70">{formatDate(data.upcoming.booking_date)} · {formatTime(data.upcoming.booking_time)}</p>
            <button onClick={() => navigate(`/brief/${data.upcoming.id}`)} className="mt-4 w-full rounded-[12px] bg-white py-2.5 text-[13px] font-semibold text-[#111111]">View brief</button>
          </Card>
        )}

        {/* History */}
        <div className="mt-5 pb-6">
          <SectionLabel className="mb-3">Consultation history</SectionLabel>
          {(!data.history || data.history.length === 0) ? (
            <p className="rounded-[14px] border border-dashed border-[#e0ddd7] py-6 text-center text-[13px] text-[#aaaaaa]">No past consultations</p>
          ) : (
            <div className="space-y-2.5" data-testid="history-list">
              {data.history.map((b) => (
                <button key={b.id} onClick={() => navigate(`/brief/${b.id}`)} className="flex w-full items-center gap-3 rounded-[14px] border border-[#e9e6e1] bg-white p-3.5 text-left active:scale-[0.99]">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-semibold text-[#111111]">{b.service_name}</p>
                    <p className="text-[12px] text-[#666666]">{formatDate(b.booking_date)}</p>
                  </div>
                  <Pill tone="neutral">Done</Pill>
                  <ChevronRight size={16} className="text-[#cfccc6]" />
                </button>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      <BottomBar>
        <PrimaryButton testid="book-again" onClick={() => navigate(`/${astrologer?.booking_slug}`)} icon={<CalendarPlus size={18} strokeWidth={2} />}>
          Book Again
        </PrimaryButton>
      </BottomBar>
    </Screen>
  );
};

export default ClientProfile;
